import { Box, Button, Chip, CircularProgress, Typography, useTheme } from "@mui/material";
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Fetch from "../../services/Fetch";
import AuthContext from "../../context/AuthContext";
import SnackbarAlert from "../../components/SnackBar";
import useSnackBar from "../../hooks/UseSnackBar";
import PageHero from "../../components/motion/PageHero";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import GridMediaCard from "../../components/cards/GridMediaCard";
import ScrollReveal from "../../components/motion/ScrollReveal";
import { useConstants } from "../../hooks/UseConstants";
import { useRequireAuth } from "../../hooks/useRequireAuth";
import { resolveCourseImage } from "../../helper/resolveMediaUrl";
import { formatCurrency } from "../../helper/formatCurrency";

function PathDetail() {
    const { host, language } = useConstants();
    const isAr = language === 'ar';
    const theme = useTheme();
    const { id } = useParams();
    const { user } = useContext(AuthContext);
    const requireAuth = useRequireAuth();
    const navigate = useNavigate();
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const [loading, setLoading] = useState(true);
    const [path, setPath] = useState(null);
    const [buying, setBuying] = useState(false);

    useEffect(() => {
        const load = async () => {
            const result = await Fetch(`${host}/paths/${id}/show`, 'GET');
            if (result.status === 200) setPath(result.data.data);
            setLoading(false);
        };
        load();
    }, [host, id]);

    const courses = useMemo(
        () => [...(path?.courses || [])].sort((a, b) => ((a.pivot?.order ?? 0) - (b.pivot?.order ?? 0))),
        [path]
    );

    const owned = !!(path?.my_enrollment?.id ?? (Array.isArray(path?.my_enrollment) && path.my_enrollment.length));
    const price = Number(path?.effective_price ?? path?.price ?? 0);
    const originalPrice = Number(path?.price ?? 0);
    const wallet = Number(user?.wallet?.balance ?? 0);
    const pathTitle = isAr ? (path?.name_ar || path?.name_en) : (path?.name_en || path?.name_ar);
    const pathDescription = isAr
        ? (path?.description_ar || path?.description_en || '')
        : (path?.description_en || path?.description_ar || '');

    const buy = async () => {
        if (!requireAuth()) return;
        if (wallet < price) {
            setSnackBar('error', isAr ? 'رصيدك غير كافٍ. يرجى شحن المحفظة أولاً' : 'Insufficient balance. Please top up your wallet first.');
            setTimeout(() => navigate('/wallet'), 1500);
            return;
        }
        setBuying(true);
        const result = await Fetch(`${host}/student/buy/path/${id}`, 'POST', {});
        if (result.status === 200) {
            setSnackBar('success', isAr ? 'تم شراء المسار بنجاح' : 'Path purchased successfully');
            const reload = await Fetch(`${host}/paths/${id}/show`, 'GET');
            if (reload.status === 200) setPath(reload.data.data);
        } else {
            setSnackBar('error', result.data?.errors?.[0] || result.data?.message || (isAr ? 'تعذر الشراء' : 'Purchase failed'));
        }
        setBuying(false);
    };

    const priceBlock = path ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
                size="small"
                label={formatCurrency(price, isAr)}
                sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700 }}
            />
            {path.has_active_discount ? (
                <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                    {formatCurrency(originalPrice, isAr)}
                </Typography>
            ) : null}
        </Box>
    ) : null;

    if (loading) return <Box className="flex justify-center py-20"><CircularProgress size={70} /></Box>;
    if (!path) return <Typography sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>{isAr ? 'المسار غير موجود' : 'Path not found'}</Typography>;

    return (
        <Box>
            <PageHero
                title={pathTitle}
                subtitle={isAr ? 'عرض دورات المسار' : 'View courses in this path'}
                icon={<AccountTreeOutlinedIcon />}
            />

            <ScrollReveal direction="down">
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 3,
                        mb: 3,
                        p: 3,
                        borderRadius: 4,
                        bgcolor: theme.palette.background.paper,
                        border: '1px solid',
                        borderColor: theme.palette.mode === 'dark' ? 'divider' : '#e2e8f0',
                        boxShadow: '0 4px 24px rgba(13,53,140,0.08)',
                    }}
                >
                    <Box
                        sx={{
                            width: { xs: '100%', md: 320 },
                            height: 200,
                            borderRadius: 3,
                            overflow: 'hidden',
                            bgcolor: '#0D358C',
                            flexShrink: 0,
                        }}
                    >
                        {path.image ? (
                            <Box
                                component="img"
                                src={resolveCourseImage(path)}
                                alt={pathTitle}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        ) : (
                            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0D358C 0%, #2563EA 100%)' }}>
                                <AccountTreeOutlinedIcon sx={{ fontSize: 64, color: 'rgba(255,255,255,0.85)' }} />
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h5" fontWeight={800} sx={{ color: '#0D358C', mb: 1 }}>
                            {pathTitle}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {path.category ? (
                                <Chip
                                    size="small"
                                    label={isAr ? path.category.name_ar : path.category.name_en}
                                    sx={{ bgcolor: '#f8fafc' }}
                                />
                            ) : null}
                            <Chip
                                size="small"
                                label={`${courses.length} ${isAr ? 'دورة' : 'courses'}`}
                                sx={{ bgcolor: '#ecfeff', color: '#0f766e' }}
                            />
                            {owned ? (
                                <Chip
                                    size="small"
                                    label={isAr ? 'مشترك' : 'Enrolled'}
                                    sx={{ bgcolor: '#f0fdf4', color: '#166534' }}
                                />
                            ) : null}
                        </Box>
                        {priceBlock}
                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#0D358C', mt: 2, mb: 0.5 }}>
                            {isAr ? 'وصف المسار' : 'Path description'}
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, whiteSpace: 'pre-wrap', mb: 2 }}>
                            {pathDescription || (isAr ? 'لا يوجد وصف للمسار بعد.' : 'No path description yet.')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            {owned ? (
                                <Button variant="contained" onClick={() => navigate('/my-courses')}>
                                    {isAr ? 'ابدأ التعلم' : 'Start learning'}
                                </Button>
                            ) : (
                                <Button variant="contained" onClick={buy} disabled={buying}>
                                    {buying ? <CircularProgress size={22} color="inherit" /> : (user ? (isAr ? 'شراء المسار' : 'Buy path') : (isAr ? 'تسجيل الدخول للشراء' : 'Login to purchase'))}
                                </Button>
                            )}
                            <Button variant="outlined" onClick={() => navigate('/paths')}>
                                {isAr ? 'العودة للمسارات' : 'Back to paths'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </ScrollReveal>

            <DashboardPanelCard
                title={isAr ? 'دورات المسار' : 'Path courses'}
                subtitle={isAr ? 'الدورات المرتبطة بهذا المسار' : 'Courses linked to this learning path'}
                className="mx-0"
            >
                {courses.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <MenuBookOutlinedIcon sx={{ fontSize: 56, color: '#94a3b8', mb: 1 }} />
                        <Typography color="text.secondary">
                            {isAr ? 'لا توجد دورات مرتبطة بهذا المسار بعد.' : 'No courses linked to this path yet.'}
                        </Typography>
                    </Box>
                ) : (
                    <Box className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                        {courses.map((course, index) => {
                            const teacherName = course.teacher
                                ? `${course.teacher.first_name || ''} ${course.teacher.last_name || ''}`.trim()
                                : '';
                            const courseTitle = isAr ? (course.name_ar || course.name_en) : (course.name_en || course.name_ar);
                            const courseDescription = isAr
                                ? (course.description_ar || course.description_en || teacherName)
                                : (course.description_en || course.description_ar || teacherName);

                            return (
                                <ScrollReveal key={course.id} delay={Math.min(index * 60, 360)} direction="up" className="w-full">
                                    <GridMediaCard
                                        image={resolveCourseImage(course)}
                                        title={courseTitle}
                                        description={teacherName || courseDescription}
                                        onClick={() => navigate(`/courses/${course.id}`)}
                                        footer={
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                                <Typography variant="caption" className="text-blue-600 font-bold">
                                                    {formatCurrency(course.effective_price ?? course.price, isAr)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                                    {isAr ? `الترتيب ${course.pivot?.order ?? index + 1}` : `Order ${course.pivot?.order ?? index + 1}`}
                                                </Typography>
                                            </Box>
                                        }
                                        actions={
                                            <Button onClick={() => navigate(`/courses/${course.id}`)} size="small">
                                                {isAr ? 'عرض' : 'View'}
                                            </Button>
                                        }
                                    />
                                </ScrollReveal>
                            );
                        })}
                    </Box>
                )}
            </DashboardPanelCard>

            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </Box>
    );
}

export default PathDetail;
